// Fetch/fetchSupplier.js

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7084';

// Helper function to get headers with auth token
const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

// Helper function to handle API responses
const handleResponse = async (response) => {
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return await response.json();
    }
    
    return null; // For responses with no content (like DELETE)
};

// Get paginated suppliers with search
export const getSuppliers = async (page = 1, pageSize = 10, search = '') => {
    try {
        const queryParams = new URLSearchParams({
            page: page.toString(),
            pageSize: pageSize.toString(),
            ...(search && { search })
        });

        const response = await fetch(`${API_BASE_URL}/api/Supplier?${queryParams}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get all suppliers (for dropdowns)
export const getAllSuppliers = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Supplier/all`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get supplier by ID
export const getSupplierById = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Supplier/${id}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Create new supplier
export const createSupplier = async (supplierData) => {
    try {
        // Clean the data before sending
        const cleanedData = cleanSupplierData(supplierData);
        
        const response = await fetch(`${API_BASE_URL}/api/Supplier`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(cleanedData)
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Update supplier
export const updateSupplier = async (id, supplierData) => {
    try {
        // Clean the data before sending
        const cleanedData = cleanSupplierData(supplierData);
        
        const response = await fetch(`${API_BASE_URL}/api/Supplier/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(cleanedData)
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Delete supplier
export const deleteSupplier = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Supplier/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get unique service types
export const getServiceTypes = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/Supplier/service-types`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Validation helper functions
export const validateSupplier = (supplierData) => {
    const errors = {};
    
    if (!supplierData.name || supplierData.name.trim().length === 0) {
        errors.name = 'שם הספק חובה';
    } else if (supplierData.name.length > 200) {
        errors.name = 'שם הספק לא יכול להיות יותר מ-200 תווים';
    }
    
    if (supplierData.contactPerson && supplierData.contactPerson.length > 200) {
        errors.contactPerson = 'שם איש הקשר לא יכול להיות יותר מ-200 תווים';
    }
    
    if (supplierData.phone && supplierData.phone.length > 20) {
        errors.phone = 'מספר הטלפון לא יכול להיות יותר מ-20 תווים';
    }
    
    // EMAIL IS NOW TRULY OPTIONAL - Only validate if not empty
    if (supplierData.email && supplierData.email.trim() !== '') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(supplierData.email.trim())) {
            errors.email = 'כתובת האימייל לא תקינה';
        } else if (supplierData.email.length > 200) {
            errors.email = 'כתובת האימייל לא יכולה להיות יותר מ-200 תווים';
        }
    }
    
    if (supplierData.serviceType && supplierData.serviceType.length > 100) {
        errors.serviceType = 'סוג השירות לא יכול להיות יותר מ-100 תווים';
    }

    // Priority validation
    if (supplierData.priority !== undefined && supplierData.priority !== null) {
        const priority = parseInt(supplierData.priority);
        if (isNaN(priority) || priority < 1 || priority > 99) {
            errors.priority = 'עדיפות חייבת להיות מספר בין 1 ל-99';
        }
    }
    
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Helper function to clean supplier data before sending to API
export const cleanSupplierData = (supplierData) => {
    const cleaned = { ...supplierData };
    
    // Completely remove email field if it's empty to avoid server validation
    if (!cleaned.email || cleaned.email.trim() === '') {
        delete cleaned.email;
    } else {
        cleaned.email = cleaned.email.trim();
    }
    
    // Clean other string fields
    if (cleaned.name) cleaned.name = cleaned.name.trim();
    if (cleaned.contactPerson) cleaned.contactPerson = cleaned.contactPerson.trim();
    if (cleaned.phone) cleaned.phone = cleaned.phone.trim();
    if (cleaned.serviceType) cleaned.serviceType = cleaned.serviceType.trim();
    
    // Ensure priority is a number
    if (cleaned.priority) {
        cleaned.priority = parseInt(cleaned.priority);
    }
    
    return cleaned;
};

// Common service types for dropdown
export const commonServiceTypes = [
    'אינסטלציה',
    'חשמל',
    'ניקיון',
    'גינון',
    'אבטחה',
    'תחזוקה כללית',
    'מעליות',
    'מעלית',
    'אינטרקום',
    'מזגנים',
    'מצלמה',
    'מצלמות',
    'צביעה',
    'נקיון חדרי מדרגות',
    'אחר'
];

// Export all functions as default object
export default {
    getSuppliers,
    getAllSuppliers,
    getSupplierById,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    getServiceTypes,
    validateSupplier,
    cleanSupplierData,
    commonServiceTypes
};